import { useRef, useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Building2, Briefcase, User, Mail, Lock, Phone, Chrome } from 'lucide-react';
import api from '../../lib/api';
import { startSocialLogin } from '../../lib/socialAuth';
import { useAuthStore } from '../../stores/authStore';
import { GlobalRole } from '../../types';
import { getRoleHomePath, requiresOrganizationOnboarding } from '../../lib/onboarding';
import { FormInput } from '../../components/ui/gaming-login';
import KolMyGlassLogo from '../../components/KolMyGlassLogo';
import { consumeInviteRedirect, rememberInviteRedirect, sanitizeAppRedirect } from '../../lib/inviteRedirect';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuthStore();
  const initialRole = (searchParams.get('role') || '').toUpperCase() === 'KOL' ? 'KOL' : 'AGENCY';
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    password: '',
    fullName: '',
    companyName: '',
    phone: '',
    role: initialRole as GlobalRole,
  });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [socialEnabled, setSocialEnabled] = useState(false);
  const submitStartedRef = useRef(false);

  const redirectSafe = sanitizeAppRedirect(searchParams.get('redirect')) || '';
  const inviteEmailLocked = redirectSafe.startsWith('/invite/') && Boolean(searchParams.get('email')?.trim());

  useEffect(() => {
    const email = searchParams.get('email')?.trim();
    const prefillName = searchParams.get('prefillName')?.trim();
    if (!email && !prefillName) return;
    setFormData((prev) => ({
      ...prev,
      ...(email ? { email } : {}),
      ...(prefillName ? { fullName: prefillName } : {}),
    }));
  }, [searchParams]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get<{ socialAuth?: { enabled?: boolean } }>(
          "/config/public",
        );
        if (active) setSocialEnabled(data?.socialAuth?.enabled === true);
      } catch {
        if (active) setSocialEnabled(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitStartedRef.current) return;
    submitStartedRef.current = true;
    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    try {
      const redirect = sanitizeAppRedirect(searchParams.get('redirect')) || consumeInviteRedirect();
      if (redirect.startsWith('/invite/')) rememberInviteRedirect(redirect);
      const result = await register(formData);
      const suggestedBase = (
        formData.companyName ||
        formData.fullName ||
        formData.email.split("@")[0] ||
        ""
      ).trim();
      if (
        suggestedBase &&
        (formData.role === "AGENCY" || formData.role === "BRAND")
      ) {
        const suffix = formData.role === "AGENCY" ? " Agency" : " Brand";
        localStorage.setItem(
          "onboarding:draftOrgName",
          `${suggestedBase}${suffix}`,
        );
      }

      if (result.needsEmailConfirmation) {
        setSuccessMessage(
          result.message ||
            `We sent a confirmation link to ${formData.email.trim()}. Open that email to finish setup, then sign in.`,
        );
        setFormData((prev) => ({ ...prev, password: "" }));
        return;
      }

      const user = result.user ?? useAuthStore.getState().user;
      if (user) {
        if (redirect) {
          navigate(redirect);
          return;
        }
        if (requiresOrganizationOnboarding(user)) {
          navigate("/onboarding/organization");
        } else {
          navigate(getRoleHomePath(user));
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to register");
    } finally {
      setIsLoading(false);
      submitStartedRef.current = false;
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="theme-agency relative min-h-screen w-full overflow-x-hidden px-4 py-10 text-glass-text login-glass-bg">
      <div className="relative z-10 mx-auto flex w-full max-w-md flex-col items-center gap-8 pb-16">
        <div className="w-full animate-fade-in">
          <div className="glass-card p-8 shadow-xl">
            <div className="mb-8 flex flex-col items-center text-center">
              <Link
                to="/"
                className="mb-3 outline-none focus-visible:rounded-lg focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-rgb)/0.55)] focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-900"
              >
                <KolMyGlassLogo variant="hero" heroScale={0.6} />
              </Link>
              <p className="text-sm text-glass-text-2">Create your account</p>
            </div>

            {socialEnabled && !successMessage ? (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={() => {
                    const redirect = sanitizeAppRedirect(searchParams.get('redirect')) || consumeInviteRedirect();
                    if (redirect.startsWith('/invite/')) rememberInviteRedirect(redirect);
                    startSocialLogin('google', redirect || '/register');
                  }}
                  disabled={isLoading}
                  className="btn-glass flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-medium text-glass-text opacity-95 transition-opacity hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Chrome className="h-5 w-5 shrink-0" aria-hidden />
                  Continue with Google
                </button>
                <div className="relative mt-6 flex items-center justify-center">
                  <div className="absolute w-full border-t border-glass" />
                  <span className="relative z-[1] rounded-full border border-glass bg-glass-2 px-3 py-1 text-xs text-glass-text-3">
                    or register with email
                  </span>
                </div>
              </div>
            ) : null}

            {error ? (
              <div className="mb-4 rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-200">
                {error}
              </div>
            ) : null}

            {successMessage ? (
              <div
                className="mb-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-3 text-sm text-emerald-800 dark:text-emerald-100"
                role="status"
              >
                <p className="font-medium">Registration in progress</p>
                <p className="mt-1 text-glass-text-2">{successMessage}</p>
                <p className="mt-3">
                  <Link
                    to="/login"
                    className="font-medium text-accent2 underline-offset-2 hover:underline"
                  >
                    Go to sign in
                  </Link>
                </p>
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label
                  htmlFor="fullName"
                  className="block text-xs font-medium uppercase tracking-wide text-glass-text-3"
                >
                  Full name
                </label>
                <FormInput
                  id="fullName"
                  name="fullName"
                  icon={<User className="text-glass-text-3" size={18} />}
                  type="text"
                  placeholder="John Doe"
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData({ ...formData, fullName: e.target.value })
                  }
                  required
                  disabled={isLoading || !!successMessage}
                />
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="email"
                  className="block text-xs font-medium uppercase tracking-wide text-glass-text-3"
                >
                  Email
                </label>
                <FormInput
                  id="email"
                  name="email"
                  icon={<Mail className="text-glass-text-3" size={18} />}
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  disabled={isLoading || !!successMessage}
                  readOnly={inviteEmailLocked}
                  title={inviteEmailLocked ? 'Use the email from your invitation' : undefined}
                />
                {inviteEmailLocked ? (
                  <p className="text-xs text-glass-text-3">
                    Email is fixed so it matches your invite. Ask your agency if this isn&apos;t correct.
                  </p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="companyName"
                  className="block text-xs font-medium uppercase tracking-wide text-glass-text-3"
                >
                  Company / organization
                </label>
                <FormInput
                  id="companyName"
                  name="companyName"
                  icon={<Building2 className="text-glass-text-3" size={18} />}
                  type="text"
                  placeholder="Acme Marketing"
                  value={formData.companyName}
                  onChange={(e) =>
                    setFormData({ ...formData, companyName: e.target.value })
                  }
                  disabled={isLoading || !!successMessage}
                />
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="phone"
                  className="block text-xs font-medium uppercase tracking-wide text-glass-text-3"
                >
                  Phone
                </label>
                <FormInput
                  id="phone"
                  name="phone"
                  icon={<Phone className="text-glass-text-3" size={18} />}
                  type="tel"
                  placeholder="+60 12-345 6789"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  disabled={isLoading || !!successMessage}
                />
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="role"
                  className="block text-xs font-medium uppercase tracking-wide text-glass-text-3"
                >
                  Role
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2">
                    <Briefcase className="text-glass-text-3" size={18} />
                  </div>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    disabled={isLoading || !!successMessage}
                    className="glass-input w-full appearance-none py-2.5 pl-10 pr-3 disabled:opacity-60 [&>option]:bg-glass-2 [&>option]:text-glass-text"
                  >
                    <option value="AGENCY">Agency</option>
                    <option value="BRAND">Brand</option>
                    <option value="KOL">KOL / Influencer</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="password"
                  className="block text-xs font-medium uppercase tracking-wide text-glass-text-3"
                >
                  Password
                </label>
                <FormInput
                  id="password"
                  name="password"
                  icon={<Lock className="text-glass-text-3" size={18} />}
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                  disabled={isLoading || !!successMessage}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !!successMessage}
                className="btn-accent w-full transform py-3 font-medium shadow-lg transition-all duration-200 ease-in-out hover:-translate-y-0.5 focus:outline-none disabled:transform-none disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isLoading
                  ? "Creating account…"
                  : successMessage
                    ? "Check your email"
                    : "Create account"}
              </button>
            </form>

            <p className="mt-8 text-center text-sm text-glass-text-3">
              Already have an account?{" "}
              <Link
                to="/login"
                className="font-medium text-accent2 transition-colors hover:underline"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <footer className="text-center text-sm text-glass-text-3">
          © {new Date().getFullYear()} QueueMart 1260221-X All rights reserved.
          ·{" "}
          <Link to="/" className="text-accent2 hover:underline">
            Home
          </Link>
        </footer>
      </div>
    </div>
  );
}
